import type { ClientConfig } from '@providers/ClientConfigProvider';

type HeadScriptsConfig = Pick<
  ClientConfig,
  | 'APP_ENV'
  | 'APP_URL'
  | 'BRANCH_NAME'
  | 'IS_STAGING'
  | 'CI_COMMIT_SHORT_SHA'
  | 'SEGMENT_API_KEY'
  | 'GTMID'
  | 'COOKIE_HANDLER_URL'
  | 'ONETRUST_DATA_DOMAIN_SCRIPT'
> & {
  COOKIE_HANDLER_URL: string;
};

/**
 * Generate all head scripts at runtime.
 * Accepts config to avoid importing Config directly (which causes issues in client components).
 */
export function getScripts(config: HeadScriptsConfig): string[] {
  const domainExtension = config.APP_ENV === 'production' ? 'ai' : 'dev';
  return [
    /* Redirect logged-in users to the app (homepage only), after collecting UTMs */
    `
    (function () {
      var isHomepage = window.location.pathname === '/' || /^\\/[a-z]{2}(-[A-Z]{2})?\\/?$/.test(window.location.pathname);
      if (!isHomepage) return;

      function getCookie(name) {
        var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
      }

      if (getCookie('loggedInApp') !== 'true') return;

      // Keys mirror apps/cookie-handler saveSourceParameters FIELDS (marketing
      // params only — no PII). Click ids (gclid/msclkid/gbraid/wbraid) count as
      // an attribution signal on their own: Google/Microsoft auto-tagging often
      // lands with a click id and no utm_* at all.
      var MARKETING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'internal_campaign', 'internal_source', 'internal_medium', 'gclid', 'msclkid', 'gbraid', 'wbraid'];

      var searchParams = {};
      new URL(window.location.href).searchParams.forEach(function (value, key) {
        searchParams[key.toLowerCase()] = value;
      });

      var hasMarketingParams = MARKETING_PARAMS.some(function (key) { return searchParams[key]; });

      function doRedirect() {
        // No anonymousId hand-off here on purpose: this inline head script
        // runs before UUIDBootstrap can set window.anonymousId, and the
        // logged-in homepage→app redirect is a same-site top-level navigation
        // so the UUID cookie travels with it under sameSite=strict anyway.
        window.location.replace("${config.APP_URL.replace(/"/g, '\\"')}");
      }

      if (hasMarketingParams) {
        var queryParams = new URLSearchParams();
        MARKETING_PARAMS.forEach(function (key) {
          if (searchParams[key]) queryParams.append(key, searchParams[key]);
        });
        if (document.referrer) queryParams.append('referrer', document.referrer);
        queryParams.append('landing_page', window.location.pathname);

        var fetchUrl = "${config.COOKIE_HANDLER_URL}/utm/set-cookie?" + queryParams.toString();
        fetch(fetchUrl, { method: 'POST', credentials: 'include' })
          .finally(doRedirect);
      } else {
        doRedirect();
      }
    })();
    `,
    /* Initialize dataLayer for GTM */
    `
    window.dataLayer = window.dataLayer || [];
    `,

    /* Loading segment script */
    `
    !function () {
        var analytics = window.analytics = window.analytics || []; if (!analytics.initialize) if (analytics.invoked) window.console && console.error && console.error("Segment snippet included twice."); else {
            analytics.invoked = !0; analytics.methods = ["trackSubmit", "trackClick", "trackLink", "trackForm", "pageview", "identify", "reset", "group", "track", "ready", "alias", "debug", "page", "screen", "once", "off", "on", "addSourceMiddleware", "addIntegrationMiddleware", "setAnonymousId", "addDestinationMiddleware", "register"]; analytics.factory = function (e) { return function () { if (window.analytics.initialized) return window.analytics[e].apply(window.analytics, arguments); var i = Array.prototype.slice.call(arguments); if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) { var c = document.querySelector("link[rel='canonical']"); i.push({ __t: "bpc", c: c && c.getAttribute("href") || void 0, p: location.pathname, u: location.href, s: location.search, t: document.title, r: document.referrer }) } i.unshift(e); analytics.push(i); return analytics } }; for (var i = 0; i < analytics.methods.length; i++) { var key = analytics.methods[i]; analytics[key] = analytics.factory(key) } analytics.load = function (key, i) { var t = document.createElement("script"); t.type = "text/javascript"; t.async = !0; t.src = "https://seg.thebrief.${domainExtension}/analytics.js/v1/" + key + "/analytics.min.js"; var n = document.getElementsByTagName("script")[0]; n.parentNode.insertBefore(t, n); analytics._loadOptions = i }; analytics._writeKey = "${config.SEGMENT_API_KEY}"; analytics.SNIPPET_VERSION = "5.2.0";
            var SegmentStatus = function ({ payload, next }) {
                const isSegmentDisabled = Boolean(JSON.parse(localStorage.getItem('autoSaveDisabled') || 'false')) || window.location.pathname.includes('/impersonate/');
                if (isSegmentDisabled) return;

                next(payload);
            };
            analytics.addSourceMiddleware(SegmentStatus);
            analytics.load("${config.SEGMENT_API_KEY}", {
                integrations: {
                    'Segment.io': {
                        deliveryStrategy: {
                            config: {
                                keepalive: true,
                            },
                        },
                        apiHost: "seg-api.thebrief.${domainExtension}/v1",
                    },
                }
            });
        }
    }();
    `,
    /* Collect query params */
    `
    // Keys mirror apps/cookie-handler saveSourceParameters FIELDS (marketing
    // params only — no PII). Click ids (gclid/msclkid/gbraid/wbraid) count as
    // an attribution signal on their own: Google/Microsoft auto-tagging often
    // lands with a click id and no utm_* at all. Params are matched
    // case-insensitively, same as apps/app parseSourceParametersFromSearch.
    const MARKETING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'internal_campaign', 'internal_source', 'internal_medium', 'gclid', 'msclkid', 'gbraid', 'wbraid'];

    const searchParams = {};
    new URL(window.location.href).searchParams.forEach((value, key) => {
        searchParams[key.toLowerCase()] = value;
    });

    if (MARKETING_PARAMS.some((key) => searchParams[key])) {
        storeSourceParameters(searchParams);
    }

    function buildSourceCookieURL(queryParams) {
        let fetchUrl = "${config.COOKIE_HANDLER_URL}/utm/set-cookie";
        if (queryParams.toString()) {
            fetchUrl += "?" + queryParams.toString();
        }
        return fetchUrl;
    }

    async function storeSourceParameters(params) {
        try {
            const queryParams = new URLSearchParams();
            MARKETING_PARAMS.forEach((key) => {
                if (params[key]) queryParams.append(key, params[key]);
            });
            if (document.referrer) queryParams.append('referrer', document.referrer);
            queryParams.append('landing_page', window.location.pathname);

            const fetchUrl = buildSourceCookieURL(queryParams);

            await fetch(fetchUrl, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (e) {
            console.error(e);
        }
    }
    `,
  ];
}
